import React, { forwardRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  labelBadge?: string;
  labelTooltip?: string;
  error?: string;
  leftIcon?: IconDefinition;
  rightIcon?: IconDefinition;
  onRightIconClick?: () => void;
  variant?: 'default' | 'search';
  required?: boolean;
  helperText?: string;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      labelBadge,
      labelTooltip,
      error,
      leftIcon,
      rightIcon,
      onRightIconClick,
      variant = 'default',
      required = false,
      helperText,
      className = '',
      id,
      ...props
    },
    ref
  ) => {
    const inputId = id || props.name || Math.random().toString(36).substr(2, 9);

    const baseClasses =
      'w-full border rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors';

    const variantClasses = {
      default: 'py-3',
      search: 'py-2'
    };

    const paddingClasses =
      leftIcon && rightIcon
        ? 'pl-10 pr-10'
        : leftIcon
          ? 'pl-10 pr-3'
          : rightIcon
            ? 'pl-3 pr-10'
            : 'px-4';

    const borderClasses = error ? 'border-red-300' : 'border-gray-300';

    const inputClasses =
      `${baseClasses} ${variantClasses[variant]} ${paddingClasses} ${borderClasses} ${className}`.trim();

    return (
      <div className="w-full">
        {label && (
          <label htmlFor={inputId} className="block text-sm font-medium text-gray-700 mb-2">
            <div className="flex items-center gap-2">
              <span>
                {label}
                {required && <span className="text-red-500 ml-1">*</span>}
              </span>
              {labelBadge && (
                <span className="bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded-full font-medium border border-green-200">
                  {labelBadge}
                </span>
              )}
              {labelTooltip && (
                <div className="group relative">
                  <span title={labelTooltip}>
                    <FontAwesomeIcon
                      icon={faInfoCircle}
                      className="w-3 h-3 text-blue-500 cursor-help"
                    />
                  </span>
                </div>
              )}
            </div>
          </label>
        )}

        <div className="relative">
          {leftIcon && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FontAwesomeIcon icon={leftIcon} className="h-4 w-4 text-gray-400" />
            </div>
          )}

          <input ref={ref} id={inputId} className={inputClasses} {...props} />

          {rightIcon && (
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
              onClick={onRightIconClick}
              tabIndex={onRightIconClick ? 0 : -1}
            >
              <FontAwesomeIcon
                icon={rightIcon}
                className="h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors"
              />
            </button>
          )}
        </div>

        {(error || helperText) && (
          <div className="mt-1">
            {error && <p className="text-red-500 text-xs">{error}</p>}
            {!error && helperText && <p className="text-gray-500 text-xs">{helperText}</p>}
          </div>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
